import { getCartonQuantity } from './cartonQuantities'

/**
 * Generate quantity options based on carton size.
 * - small quantities step by 1 up to a carton-aligned threshold
 * - larger quantities step by the carton size for efficient ordering
 *
 * The carton size comes from the Xhosa API (product.cartonQuantity). POS materials are capped at 1.
 *
 * `maxOrderable` caps the options for NON-POS products at the hard orderable limit (immediate
 * available + usable replenishment). When it is a finite number ≥ 0, options above it are dropped
 * and the cap itself is always selectable as the last option. null/undefined keeps the default 1000.
 */
export function generateQuantityOptions(cartonQuantity = 1, isPos = false, maxOrderable?: number | null): number[] {
  const options: number[] = []

  // POS materials are capped at 1 per SKU (feedback #07): only 0 or 1 is selectable.
  if (isPos) {
    return [0, 1]
  }

  const DEFAULT_THRESHOLD = 100
  const MAX_QUANTITY = 1000
  const cartonQty = getCartonQuantity(cartonQuantity)

  // Individual items (carton size 1 or unknown): 0-100 by 1, then 110-1000 by 10.
  if (cartonQty <= 1) {
    for (let i = 0; i <= DEFAULT_THRESHOLD; i++) {
      options.push(i)
    }
    for (let i = DEFAULT_THRESHOLD + 10; i <= MAX_QUANTITY; i += 10) {
      options.push(i)
    }
    return capOptions(options, maxOrderable)
  }

  // Transition to carton-based steps at a natural boundary (largest multiple of cartonQty ≤ 100).
  let threshold = DEFAULT_THRESHOLD
  if (cartonQty <= DEFAULT_THRESHOLD) {
    threshold = Math.floor(DEFAULT_THRESHOLD / cartonQty) * cartonQty
  }

  for (let i = 0; i <= threshold; i++) {
    options.push(i)
  }

  const maxCartonQuantity = Math.floor(MAX_QUANTITY / cartonQty) * cartonQty
  for (let i = threshold + cartonQty; i <= maxCartonQuantity; i += cartonQty) {
    options.push(i)
  }

  return capOptions(options, maxOrderable)
}

/**
 * Cap a generated option list at the hard orderable limit. Options above the cap are removed, and
 * the cap itself is appended (once) as the last selectable option so the partner can always pick
 * exactly the maximum. A null/undefined or non-finite cap leaves the options unchanged.
 */
function capOptions(options: number[], maxOrderable?: number | null): number[] {
  if (typeof maxOrderable !== 'number' || !Number.isFinite(maxOrderable) || maxOrderable < 0) {
    return options
  }

  const cap = Math.floor(maxOrderable)
  const capped = options.filter((qty) => qty <= cap)

  if (!capped.includes(cap)) {
    capped.push(cap)
  }

  return capped
}
